import { usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import React, { ReactElement, useCallback, useRef, useState } from "react";
import { SpeechTranscriptItem } from "../../model/hubDtos";
import { SpeechConfirmationConfig } from "../../model/speechConfirmation";
import TranscriptConfirmAndEdit from "../presenters/TranscriptConfirmAndEdit";
import { InputContainerProps, InputInterfaceRef } from "./InputContainer";

export interface TranscriptConfirmationContainerProps extends InputContainerProps {
  config: SpeechConfirmationConfig;
  isAgentSpeaking: boolean;
  spokenTranscript: SpeechTranscriptItem | null;
  sendTextMessage(text: string): void;
}

export default function TranscriptConfirmationContainer({ componentRef, showQuickResponses, showSuggestedAnswer, onShowQuickResponses, onShowSuggestedAnswer, config, isAgentSpeaking, spokenTranscript, sendTextMessage }: TranscriptConfirmationContainerProps): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [aggregatedText, setAggregatedText] = useState("");
  const [projectedSendDate, setProjectedSendDate] = useState<Date | null>(null);

  const sendTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<InputInterfaceRef>(null);

  const clearCountdown = useCallback(
    () => {
      if (sendTimeoutRef.current)
        clearTimeout(sendTimeoutRef.current);

      sendTimeoutRef.current = null;
      setProjectedSendDate(null);
    },
    [],
  );

  const handleSend = useCallback(
    () => {
      sendTextMessage(aggregatedText);
      setAggregatedText("");
      setIsEditing(false);

      clearCountdown();
    },
    [aggregatedText, clearCountdown, sendTextMessage],
  );

  const startCountdown = useCallback(
    () => {
      if (aggregatedText.trim() === "" || isAgentSpeaking)
        return;

      if (sendTimeoutRef.current)
        clearTimeout(sendTimeoutRef.current);

      const timeoutMilliseconds = config.timeoutSeconds * 1000;
      sendTimeoutRef.current = setTimeout(handleSend, timeoutMilliseconds);
      setProjectedSendDate(new Date(Date.now() + timeoutMilliseconds));
    },
    [aggregatedText, config.timeoutSeconds, handleSend, isAgentSpeaking],
  );

  // handle countdown set/clear when isAgentSpeaking changes
  usePureEffect(
    (refs) => {
      if (refs.isEditing || !refs.aggregatedText)
        return;

      if (!isAgentSpeaking)
        refs.startCountdown();
      else
        refs.clearCountdown();
    },
    [isAgentSpeaking],
    { aggregatedText, isEditing, startCountdown, clearCountdown },
  );

  // add text to aggregatedText when spokenTranscript changes while not editing
  usePureEffect(
    (refs) => {
      if (!refs.isEditing && spokenTranscript) {
        setAggregatedText(prev => prev ? prev + " " + spokenTranscript.text : spokenTranscript.text);
      }
    },
    [spokenTranscript],
    { isEditing },
  );

  // This is a safety fallback to ensure countdown starts in all cases
  // - activated in cases where ReceiveAgentTranscriptItemForConfirmation(...) event arrives after the ReceiveAgentSpeechStatus({ isSpeaking: false }) event
  // - Google STT provider works like this
  usePureEffect(
    (refs) => {
      if (aggregatedText && !refs.isEditing && !refs.isAgentSpeaking) {
        refs.startCountdown();
      }
    },
    [aggregatedText],
    { isEditing, isAgentSpeaking, startCountdown },
  );

  const handleClear = useCallback(
    () => {
      clearCountdown();

      setAggregatedText("");
      setIsEditing(false);
    },
    [clearCountdown],
  );

  const handleSwitchToEditMode = useCallback(
    () => {
      clearCountdown();
      setIsEditing(true);
    },
    [clearCountdown],
  );

  const handleSwitchToVoiceMode = useCallback(
    () => {
      setIsEditing(false);

      if (aggregatedText)
        startCountdown();
    },
    [aggregatedText, startCountdown],
  );

  if (componentRef) {
    (componentRef as React.MutableRefObject<InputInterfaceRef>).current = {
      setInputTextAndFocus: (text) => {
        setAggregatedText(text);
        inputRef.current?.setInputTextAndFocus(text);
      },
    };
  }

  return (
    <TranscriptConfirmAndEdit
      componentRef={inputRef}
      showQuickResponses={showQuickResponses}
      showSuggestedAnswer={showSuggestedAnswer}
      onShowQuickResponses={onShowQuickResponses}
      onShowSuggestedAnswer={onShowSuggestedAnswer}
      isEditing={isEditing}
      isAgentSpeaking={isAgentSpeaking}
      projectedSendDate={projectedSendDate}
      sendTimeoutSeconds={config.timeoutSeconds}
      text={aggregatedText}
      onTextChange={setAggregatedText}
      onSend={handleSend}
      onClear={handleClear}
      onSwitchToEditMode={handleSwitchToEditMode}
      onSwitchToVoiceMode={handleSwitchToVoiceMode}
    />
  );
}
